/* eslint-disable react/jsx-no-target-blank */ /* NTCONT-737 customer request. */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import propTypes from 'prop-types';

import { useConfig, useBuyingOptions } from '../../../hooks';

import ListItem from './ListItem.js';
import ListGroup from './ListGroup';
import GeolocationButton from '../../GeolocationButton';
import { enforceProtocol, sortDealersClosestNADFirst } from '../../../lib/utils';
import DealerPurchaseSection from './DealerPurchaseSection';

function OnlineDealer({ dealer }) {
  const { t } = useTranslation('dealers');
  const buyingOptionsProduct = useBuyingOptions();

  return (<>
    <a
      href={enforceProtocol(dealer.website_url)}
      target="_blank"
      rel="noopener"
      className={`dealer online-dealer${dealer.ecom ? ' online-dealer--with-ecom' : ''}`}
    >
      {dealer.icon && dealer.icon.active_marker && (
        <img
          className="dealers-icon"
          src={dealer.icon.active_marker}
          alt={dealer.company_name}
        />
      )}

      <div className="details">
        <h4>{dealer.company_name}</h4>
        <p>{t('Online dealer')}</p>
        {dealer.telephone !== '' && <p>{t('Tel')}. {dealer.telephone}</p>}
        <span className="link-wrapper">
          <span className="link">{dealer.website_url}</span>
        </span>
      </div>
    </a>
     {dealer.ecom && <DealerPurchaseSection
          dealer={dealer}
          product={dealer.ecom}
          buyingOptionsProduct={buyingOptionsProduct || null}
          loading={false}
          headingLevel={3}
          context="webstore"
        />}
    </>
  );
}

OnlineDealer.propTypes = {
  dealer: propTypes.object,
};

function NoDealers() {
  const { t } = useTranslation('dealers');

  return (
    <div className="dealer no-results disabled" key={'no-results'}>
      <div className="details">
        <h4>{t('No dealers found')}</h4>
        <p>{t('No dealers found description')}</p>
        <p>&nbsp;</p>
      </div>
    </div>
  )
}

function DealerSearchInfo({ onUserLocationChange }) {
  const { t } = useTranslation('dealers');
  const { config } = useConfig();

  return (
    <div className="dealer no-location disabled" key={'no-results'}>
      <div className="details">
        <h4>{t('Search dealers')}</h4>
        <p>{config.enableMobileMap ? t('Search help text') : t('Search help text no map')}</p>
        <p>&nbsp;</p>
        <p><GeolocationButton onLocate={onUserLocationChange} /></p>
      </div>
    </div>
  );
}

DealerSearchInfo.propTypes = {
  onUserLocationChange: propTypes.func,
};

function DealerSkeleton() {
  return (
    <div className="dealer loading disabled">
      <div className="details">
        <h4>&nbsp;</h4>
        <p style={{width: '70%'}}>&nbsp;</p>
        <p style={{width: '40%'}}>&nbsp;</p>
      </div>
      <div className="distance">
        <p>&nbsp;</p>
      </div>
    </div>
  );
}

function DealerError() {
  const { t } = useTranslation('dealers');

  return (
    <div className="dealer error disabled">
      <div className="details">
        <h4>{t('No dealers found')}</h4>
        <p>{t('Something went wrong')}</p>
      </div>
    </div>
  );
}

function DealerList({ dealers, sorting, loading, error, onlineDealers, location, onActiveDealerChange, onUserLocationChange }) {
  const { t } = useTranslation('dealers');

  const sortedDealers = useMemo(() => {
    let newDealers = dealers ? [...dealers] : dealers;
    if (sorting.type === 'nad_first' && newDealers) {
      newDealers = sortDealersClosestNADFirst(newDealers, {amount: sorting.amount, radius: sorting.radius});
    }
    return newDealers;
  }, [dealers, sorting]);

  const renderedOnlineDealers = onlineDealers.map(item => <OnlineDealer key={item.slug} dealer={item} />);

  let renderedDealers = null;
  if (loading) {
    renderedDealers = new Array(20).fill(null).map((a, i) => <DealerSkeleton key={i} />);
  }
  else if (error) {
    renderedDealers = <DealerError />;
  }
  else if (sortedDealers === null) {
    renderedDealers = <DealerSearchInfo onUserLocationChange={onUserLocationChange} />;
  }
  else if (sortedDealers.length === 0) {
    renderedDealers = <NoDealers />;
  }
  else {
    renderedDealers = sortedDealers.map((dealer, i) => <ListItem key={dealer.slug + i} dealer={dealer} location={location} onActiveDealerChange={onActiveDealerChange} />);
  }

  return (
    <>
      {renderedOnlineDealers.length > 0 && (
        <ListGroup title={t('Online dealers')} toggleLabel={t('Show online dealers')}>
          {renderedOnlineDealers}
        </ListGroup>
      )}

      <ListGroup title={t('Dealers closest to you')} toggleLabel={t('Show dealers')} open={true} alwaysOpen={onlineDealers.length === 0}>
        {renderedDealers}
      </ListGroup>
    </>
  );
}

DealerList.propTypes = {
  dealers: propTypes.array,
  sorting: propTypes.shape({
    type: propTypes.string,
    radius: propTypes.number,
    amount: propTypes.number,
  }),
  loading: propTypes.bool,
  error: propTypes.string,
  onlineDealers: propTypes.array,
  location: propTypes.object,
  onActiveDealerChange: propTypes.func,
  onUserLocationChange: propTypes.func,
};

export default DealerList;
